'use client';

import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { pressureCardEntrance } from '@/config/pressure-card-entrance';
import { GA_EVENTS, GA_EXPANDED, GA_PARAMS } from '@/constants/analytics';
import { PRESSURE_CARD_HOVER_MEDIA } from '@/constants/pressureCards';
import { useDiscernibleInView } from '@/hooks/useDiscernibleInView';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { easeStandard } from '@/motion/tokens';

import { PressureCard } from './PressureCard';
import styles from './PressureCards.module.css';

import type { ResolvedPressureCard } from './PressureCard';

type PressureCardsProps = {
  cards: ResolvedPressureCard[];
};

const ENTRANCE_VARIANTS = {
  hidden: {
    opacity: 0,
    y: pressureCardEntrance.y,
    rotate: pressureCardEntrance.rotateDeg,
  },
  visible: {
    opacity: 1,
    y: 0,
    rotate: 0,
  },
} as const;

export function PressureCards({ cards }: PressureCardsProps) {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const selectedIdsRef = useRef(selectedIds);
  const reducedMotion = usePrefersReducedMotion();
  const isDesktopGrid = useMatchMedia(PRESSURE_CARD_HOVER_MEDIA);
  const skipEntrance = reducedMotion || !isDesktopGrid;
  const { ref, inView } = useDiscernibleInView<HTMLDivElement>({
    once: true,
    topAt: pressureCardEntrance.topAt,
    disabled: skipEntrance,
  });

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const handleToggle = useCallback((id: string) => {
    const current = selectedIdsRef.current;
    const expanded = !current.has(id);
    const next = new Set(current);
    if (expanded) {
      next.add(id);
    } else {
      next.delete(id);
    }
    selectedIdsRef.current = next;
    setSelectedIds(next);
    trackEvent(GA_EVENTS.pressureCardToggle, {
      [GA_PARAMS.pressureCardId]: id,
      [GA_PARAMS.expanded]: expanded ? GA_EXPANDED.true : GA_EXPANDED.false,
    });
  }, []);

  return (
    <div ref={ref} className={styles.wrap}>
      <ul className={styles.grid}>
        {cards.map((card, index) =>
          skipEntrance ? (
            <li key={card.id} className={styles.item}>
              <PressureCard
                card={card}
                selected={selectedIds.has(card.id)}
                onToggle={handleToggle}
              />
            </li>
          ) : (
            <motion.li
              key={card.id}
              className={styles.item}
              style={{ transformOrigin: pressureCardEntrance.transformOrigin }}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              variants={ENTRANCE_VARIANTS}
              transition={{
                duration: pressureCardEntrance.duration,
                ease: easeStandard,
                delay: index * pressureCardEntrance.staggerSeconds,
              }}
            >
              <PressureCard
                card={card}
                selected={selectedIds.has(card.id)}
                onToggle={handleToggle}
              />
            </motion.li>
          ),
        )}
      </ul>
    </div>
  );
}
